import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/services/api';
import { Skeleton, DataTable, EmptyState } from '@connor-music/shared';
import type { Column } from '@connor-music/shared';
import type { SecurityOverview, ComplianceStatus, ComplianceControl, DownloadLog, Project, User } from '@shared/types';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  ShieldAlert,
  Lock,
  KeyRound,
  Key,
  Fingerprint,
  Database,
  Link2,
  GitBranch,
  Download,
  FileText,
  Archive,
  Trash2,
  Server,
  Cpu,
  Globe,
  Timer,
  EyeOff,
  GitPullRequest,
  FileCode,
  Package,
  PackageCheck,
  Search,
  Gauge,
  Eye,
  FileSearch,
  UserCheck,
  Bell,
  Award,
  RefreshCw,
  AlertCircle,
  FileDown,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
} from 'lucide-react';

type SecurityTab = 'overview' | 'authentication' | 'data-protection' | 'infrastructure' | 'compliance' | 'audit-log';

export default function AdminSecurity() {
  const [data, setData] = useState<SecurityOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<SecurityTab>('overview');

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await apiClient.getSecurityOverview();
      setData(result);
    } catch (err) {
      console.error('Failed to load security overview:', err);
      setError('Failed to load security overview.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const tabs: { key: SecurityTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'authentication', label: 'Authentication' },
    { key: 'data-protection', label: 'Data Protection' },
    { key: 'infrastructure', label: 'Infrastructure' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'audit-log', label: 'Audit Log' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
            <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Security</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Security posture, controls, and compliance status.
            </p>
          </div>
        </div>
        <button
          onClick={loadData}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-lg bg-white dark:bg-gray-800 p-4 shadow space-y-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-24" />
              </div>
            ))}
          </div>
          <Skeleton className="h-5 w-40" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-4 w-40" />
                </div>
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        </div>
      ) : data && (
        <>
          {activeTab === 'overview' && <OverviewTab data={data} />}
          {activeTab === 'authentication' && <AuthenticationTab data={data} />}
          {activeTab === 'data-protection' && <DataProtectionTab data={data} />}
          {activeTab === 'infrastructure' && <InfrastructureTab data={data} />}
          {activeTab === 'compliance' && <ComplianceTab data={data} />}
          {activeTab === 'audit-log' && <AuditLogTab />}
        </>
      )}
      {activeTab === 'audit-log' && !isLoading && !data && <AuditLogTab />}
    </div>
  );
}

// =============================================================================
// Overview Tab
// =============================================================================

// Map control_id to an icon
const CONTROL_ICONS: Record<string, React.ReactNode> = {
  's3-encryption-at-rest': <Lock className="h-4 w-4" />,
  'tls-in-transit': <Lock className="h-4 w-4" />,
  'customer-mfa-required': <ShieldCheck className="h-4 w-4" />,
  'employee-sso': <Fingerprint className="h-4 w-4" />,
  'per-project-bucket-isolation': <Database className="h-4 w-4" />,
  'presigned-url-access': <Link2 className="h-4 w-4" />,
  's3-public-access-blocked': <ShieldOff className="h-4 w-4" />,
  's3-versioning': <GitBranch className="h-4 w-4" />,
  'per-user-download-permissions': <Download className="h-4 w-4" />,
  'download-audit-logging': <FileText className="h-4 w-4" />,
  'glacier-archival': <Archive className="h-4 w-4" />,
  'soft-delete-lifecycle': <Trash2 className="h-4 w-4" />,
  'iam-least-privilege': <Shield className="h-4 w-4" />,
  'github-oidc': <Key className="h-4 w-4" />,
  'environment-isolation': <Server className="h-4 w-4" />,
  'serverless-architecture': <Cpu className="h-4 w-4" />,
  'http2-http3': <Globe className="h-4 w-4" />,
  'dynamodb-pitr': <Database className="h-4 w-4" />,
  'otp-rate-limiting': <Timer className="h-4 w-4" />,
  'anti-enumeration': <EyeOff className="h-4 w-4" />,
  'forced-password-change': <KeyRound className="h-4 w-4" />,
  'cicd-testing-pipeline': <GitPullRequest className="h-4 w-4" />,
};

const plannedControls = [
  { icon: <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'AWS WAF', description: 'Web Application Firewall on CloudFront and API Gateway' },
  { icon: <FileCode className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'Security Headers', description: 'CSP, HSTS, X-Frame-Options, X-Content-Type-Options' },
  { icon: <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'Dependabot', description: 'Automated dependency vulnerability scanning' },
  { icon: <Search className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'SAST Scanning', description: 'Static analysis with CodeQL and Bandit' },
  { icon: <PackageCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'Dependency Auditing', description: 'npm audit and pip audit in CI pipeline' },
  { icon: <Gauge className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'API Rate Limiting', description: 'Account-wide request throttling' },
  { icon: <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'CloudTrail', description: 'Full AWS API audit trail' },
  { icon: <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'GuardDuty / SecurityHub', description: 'Threat detection and security findings' },
  { icon: <FileSearch className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'S3 Access Logging', description: 'Detailed access logs for all project buckets' },
  { icon: <UserCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'Login Audit Trail', description: 'Complete authentication event history' },
  { icon: <Bell className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'Security Alerting', description: 'CloudWatch alarms for security events' },
  { icon: <Award className="h-4 w-4 text-blue-600 dark:text-blue-400" />, title: 'SOC 2 Readiness', description: 'Compliance controls and evidence collection' },
];

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFullTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function OverviewTab({ data }: { data: SecurityOverview }) {
  const [compliance, setCompliance] = useState<ComplianceStatus | null>(null);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(true);
  const [isRunningCheck, setIsRunningCheck] = useState(false);
  const [complianceError, setComplianceError] = useState('');

  useEffect(() => {
    const loadCompliance = async () => {
      setIsLoadingCompliance(true);
      try {
        const result = await apiClient.getComplianceStatus();
        setCompliance(result);
      } catch (err) {
        console.error('Failed to load compliance status:', err);
        setComplianceError('Failed to load compliance status.');
      } finally {
        setIsLoadingCompliance(false);
      }
    };
    loadCompliance();
  }, []);

  const handleRunCheck = async () => {
    setIsRunningCheck(true);
    setComplianceError('');
    try {
      const result = await apiClient.runComplianceCheck();
      setCompliance(result);
    } catch (err) {
      console.error('Failed to run compliance check:', err);
      setComplianceError('Failed to run compliance check.');
    } finally {
      setIsRunningCheck(false);
    }
  };

  const controls = compliance?.controls ?? [];
  const summary = compliance?.summary;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          icon={<Lock className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
          label="Encrypted Buckets"
          value={`${data.encrypted_buckets}/${data.total_buckets}`}
          bgClass="bg-blue-100 dark:bg-blue-900/50"
        />
        <SummaryCard
          icon={<ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />}
          label="Compliance Score"
          value={
            summary
              ? `${summary.compliant + summary.by_design}/${summary.total}`
              : '---'
          }
          bgClass="bg-green-100 dark:bg-green-900/50"
        />
        <SummaryCard
          icon={<Download className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
          label="Download Access"
          value={`${data.users_with_download_access} of ${data.total_employees}`}
          bgClass="bg-amber-100 dark:bg-amber-900/50"
        />
        <SummaryCard
          icon={<FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
          label="Audit Log"
          value={`${data.total_downloads_logged} entries`}
          bgClass="bg-purple-100 dark:bg-purple-900/50"
        />
      </div>

      {/* Compliance Check Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Security Controls
          </h2>
          {compliance?.checked_at && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Last verified: {formatFullTimestamp(compliance.checked_at)}
              {compliance.checked_by && ` by ${compliance.checked_by}`}
            </p>
          )}
        </div>
        <button
          onClick={handleRunCheck}
          disabled={isRunningCheck || isLoadingCompliance}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600"
        >
          {isRunningCheck ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="h-3.5 w-3.5" />
          )}
          {isRunningCheck ? 'Running...' : 'Run Compliance Check'}
        </button>
      </div>

      {/* Compliance Error */}
      {complianceError && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{complianceError}</span>
        </div>
      )}

      {/* Controls Loading */}
      {isLoadingCompliance ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Active Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {controls.map((control) => (
              <ComplianceControlCard key={control.control_id} control={control} />
            ))}
          </div>

          {/* Planned Controls */}
          {plannedControls.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 mt-2">
                Planned Controls
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plannedControls.map((control) => (
                  <SecurityControlCard
                    key={control.title}
                    icon={control.icon}
                    title={control.title}
                    description={control.description}

                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Authentication Tab
// =============================================================================

function AuthenticationTab({ data }: { data: SecurityOverview }) {
  return (
    <div className="space-y-6">
      {/* Admin Bootstrap Authentication */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/50">
            <KeyRound className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Admin Bootstrap Authentication
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Password Hashing" value="PBKDF2-HMAC-SHA256 (100,000 iterations)" />
          <DetailRow label="First Login" value="Forced password change on first login" />
          <DetailRow label="Access Tokens" value="JWT tokens (1-hour access, 7-day refresh)" />
          <DetailRow label="Signing Key" value="Signing key from AWS Secrets Manager" />
        </div>
      </div>

      {/* Employee SSO */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50">
              <Fingerprint className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Employee SSO (Cognito Pool A)
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            {data.total_employees} employees
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Protocol" value="SAML with Microsoft Entra ID" />
          <DetailRow label="Self-Signup" value="Disabled" />
          <DetailRow label="Token Revocation" value="Enabled" />
          <DetailRow label="User Existence Errors" value="Prevention enabled" />
          <DetailRow label="SSO Configured" value={data.sso_configured ? 'Yes' : 'No'} />
        </div>
      </div>

      {/* Customer Authentication */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/50">
              <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Customer Authentication (Cognito Pool B)
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
            {data.total_customers} customers
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="MFA" value="Required (TOTP)" />
          <DetailRow label="OTP Format" value="6-digit cryptographic OTP" />
          <DetailRow label="OTP TTL" value="10-minute OTP TTL (single-use)" />
          <DetailRow label="Rate Limiting" value="Max 5 requests per 15 min" />
          <DetailRow label="Auth Flow" value="SRP authentication only" />
          <DetailRow label="Self-Signup" value="Disabled" />
          <DetailRow label="Error Responses" value="Anti-enumeration responses" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Data Protection Tab
// =============================================================================

function DataProtectionTab({ data }: { data: SecurityOverview }) {
  return (
    <div className="space-y-6">
      {/* Bucket Isolation */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50">
              <Database className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Bucket Isolation
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            {data.total_buckets} project buckets
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Strategy" value="Each audit project receives a dedicated S3 bucket" />
          <DetailRow label="Naming" value="connor-audit-{project-id}-{account-id}" />
          <DetailRow label="Customer Access" value="Customers only access their project bucket via presigned URLs" />
        </div>
      </div>

      {/* Encryption */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/50">
              <Lock className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Encryption
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
            {data.encrypted_buckets}/{data.total_buckets} encrypted
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Algorithm" value="SSE-S3 (AES-256) with S3 Bucket Key optimization" />
          <DetailRow label="Enforcement" value="Server-side encryption enforced on all multipart uploads" />
          <DetailRow label="Policy" value="Encryption cannot be disabled at the bucket level" />
        </div>
      </div>

      {/* Access Control */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/50">
              <KeyRound className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Access Control
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {data.users_with_download_access} users with download access
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="URL Expiry" value="Presigned URLs only (5-minute upload, 1-hour download expiry)" />
          <DetailRow label="Default Access" value="Per-user download_allowed flag (defaults to disabled)" />
          <DetailRow label="Admin Control" value="Admin-only toggle for download permissions" />
          <DetailRow label="Credentials" value="Customers never receive direct AWS credentials" />
        </div>
      </div>

      {/* Lifecycle Management */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/50">
            <Archive className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Lifecycle Management
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Archival" value="Glacier Deep Archive transition after 365 days" />
          <DetailRow label="Soft-Delete" value="30-day soft-delete on removed project buckets" />
          <DetailRow label="Cleanup" value="Scheduled Lambda cleanup at 3 AM UTC daily" />
        </div>
      </div>

      {/* Public Access */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/50">
            <ShieldOff className="h-4 w-4 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Public Access
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="BlockPublicAcls" value="Enabled" />
          <DetailRow label="IgnorePublicAcls" value="Enabled" />
          <DetailRow label="BlockPublicPolicy" value="Enabled" />
          <DetailRow label="RestrictPublicBuckets" value="Enabled" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Infrastructure Tab
// =============================================================================

function InfrastructureTab({ data }: { data: SecurityOverview }) {
  return (
    <div className="space-y-6">
      {/* Serverless Architecture */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50">
            <Cpu className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Serverless Architecture
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Compute" value="Lambda (ARM64/Graviton) — no persistent servers" />
          <DetailRow label="API" value="HTTP API Gateway — managed scaling" />
          <DetailRow label="Database" value="DynamoDB — pay-per-request, point-in-time recovery" />
          <DetailRow label="CDN" value="CloudFront CDN — global edge distribution" />
        </div>
      </div>

      {/* Environment Isolation */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/50">
              <Server className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Environment Isolation
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
            Current: {data.environment}
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Accounts" value="Three separate AWS accounts (development, testing, production)" />
          <DetailRow label="Cross-Account" value="No cross-account access" />
          <DetailRow label="Secrets" value="Environment-specific secrets and configuration" />
        </div>
      </div>

      {/* Transport Security */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/50">
            <Globe className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Transport Security
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="HTTPS" value="HTTPS enforced (automatic HTTP redirect)" />
          <DetailRow label="Protocols" value="HTTP/2 and HTTP/3 support" />
          <DetailRow label="Certificates" value="ACM certificates with DNS validation" />
          <DetailRow label="Origin Access" value="CloudFront Origin Access Control with SigV4 signing" />
        </div>
      </div>

      {/* CI/CD Pipeline */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/50">
            <GitPullRequest className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            CI/CD Pipeline
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Pipeline" value="Three-stage pipeline: test > deploy-infra > deploy-frontend" />
          <DetailRow label="Authentication" value="GitHub OIDC federation (no stored AWS credentials)" />
          <DetailRow label="Linting" value="Zero-warning ESLint enforcement" />
          <DetailRow label="Type Safety" value="TypeScript strict type checking" />
        </div>
      </div>

      {/* Planned */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Planned
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SecurityControlCard
            icon={<Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="AWS WAF"
            description="AWS WAF on CloudFront and API Gateway"

          />
          <SecurityControlCard
            icon={<FileCode className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="Security Headers"
            description="Security headers (CSP, HSTS, X-Frame-Options)"

          />
          <SecurityControlCard
            icon={<Gauge className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="API Rate Limiting"
            description="API-wide rate limiting"

          />
          <SecurityControlCard
            icon={<Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="GuardDuty / SecurityHub"
            description="GuardDuty / SecurityHub integration"

          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Compliance Tab
// =============================================================================

function ComplianceTab({ data }: { data: SecurityOverview }) {
  return (
    <div className="space-y-6">
      {/* Download Audit Log */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/50">
              <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Download Audit Log
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
            {data.total_downloads_logged} entries
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow
            label="Coverage"
            value="Every file download is logged with user, file, project, timestamp, IP address, and user agent"
          />
          <DetailRow
            label="Viewer"
            value="See the Audit Log tab for the full download history with filters"
          />
        </div>
      </div>

      {/* Data Loss Prevention */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/50">
              <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Data Loss Prevention
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {data.users_with_download_access} of {data.total_employees} users with download access
          </span>
        </div>
        <div className="px-6 py-4 space-y-3">
          <DetailRow label="Download Permissions" value="Per-user download permissions (default: disabled)" />
          <DetailRow label="Admin Control" value="Admin-only toggle to enable/disable per user" />
          <DetailRow label="Delete Permission" value="Delete project permission controlled separately" />
        </div>
      </div>

      {/* Planned */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Planned
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SecurityControlCard
            icon={<Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="CloudTrail"
            description="Full AWS API audit trail"

          />
          <SecurityControlCard
            icon={<UserCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="Login Audit Trail"
            description="Complete authentication event history"

          />
          <SecurityControlCard
            icon={<Award className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="SOC 2 Readiness"
            description="Compliance controls and evidence collection"

          />
          <SecurityControlCard
            icon={<Bell className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="Security Alerting"
            description="CloudWatch alarms for security events"

          />
          <SecurityControlCard
            icon={<FileSearch className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="S3 Access Logging"
            description="Detailed access logs for all project buckets"

          />
          <SecurityControlCard
            icon={<Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            title="Dependency Scanning"
            description="Dependabot, CodeQL, Bandit, npm/pip audit"

          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Audit Log Tab
// =============================================================================

function AuditLogTab() {
  const [logs, setLogs] = useState<DownloadLog[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [logError, setLogError] = useState('');

  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [projectData, userData] = await Promise.all([
          apiClient.getProjects(),
          apiClient.getUsers(),
        ]);
        setProjects(projectData);
        setUsers(userData);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadOptions();
  }, []);

  const loadLogs = useCallback(
    async (append = false) => {
      const loading = append ? setIsLoadingMore : setIsLoadingLogs;
      loading(true);
      setLogError('');

      try {
        const params: { project_id?: string; user_id?: string; limit?: number; cursor?: string } = {
          limit: 25,
        };
        if (filterProjectId) params.project_id = filterProjectId;
        if (filterUserId) params.user_id = filterUserId;
        if (append && cursor) params.cursor = cursor;

        const result = await apiClient.getDownloadLog(params);

        if (append) {
          setLogs((prev) => [...prev, ...result.items]);
        } else {
          setLogs(result.items);
        }

        setCursor(result.cursor);
        setHasMore(!!result.cursor);
      } catch (err) {
        console.error('Failed to load download log:', err);
        setLogError('Failed to load download log.');
      } finally {
        loading(false);
      }
    },
    [filterProjectId, filterUserId, cursor],
  );

  useEffect(() => {
    setCursor(undefined);
    setHasMore(false);
    loadLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProjectId, filterUserId]);

  const formatTimestamp = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const selectClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-primary-400 dark:focus:ring-primary-400';

  const columns: Column<DownloadLog & Record<string, unknown>>[] = [
    {
      key: 'file_name',
      label: 'File Name',
      render: (value) => (
        <div className="flex items-center gap-2">
          <FileDown className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          <span className="truncate max-w-[200px] font-medium text-gray-900 dark:text-white">
            {value as string}
          </span>
        </div>
      ),
    },
    {
      key: 'downloaded_by_name',
      label: 'Downloaded By',
      render: (_value, item) => (
        <div>
          <p className="text-sm text-gray-900 dark:text-white">{item.downloaded_by_name as string}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{item.downloaded_by_email as string}</p>
        </div>
      ),
    },
    {
      key: 'project_id',
      label: 'Project',
      render: (value) => {
        const project = projects.find((p) => p.id === value);
        return (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {project?.name ?? (value as string)}
          </span>
        );
      },
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatTimestamp(value as string)}
        </span>
      ),
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      render: (value) => (
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
          {(value as string) || '---'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label
            htmlFor="filter-project"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Filter by Project
          </label>
          <select
            id="filter-project"
            value={filterProjectId}
            onChange={(e) => setFilterProjectId(e.target.value)}
            className={selectClasses}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="filter-user"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Filter by User
          </label>
          <select
            id="filter-user"
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            className={selectClasses}
          >
            <option value="">All Users</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name} ({u.email})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error */}
      {logError && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{logError}</span>
        </div>
      )}

      {/* Table */}
      {!isLoadingLogs && logs.length === 0 ? (
        <EmptyState
          icon={<Download className="h-12 w-12" />}
          title="No download history"
          description={
            filterProjectId || filterUserId
              ? 'No downloads match your current filters. Try adjusting or removing filters.'
              : 'No files have been downloaded yet. Downloads will appear here once employees download audit files.'
          }
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={logs as unknown as (DownloadLog & Record<string, unknown>)[]}
            loading={isLoadingLogs}
            emptyMessage="No download records found."
          />

          {hasMore && (
            <div className="flex justify-center">
              <button
                onClick={() => loadLogs(true)}
                disabled={isLoadingMore}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                {isLoadingMore ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-4">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-40 flex-shrink-0">
        {label}
      </span>
      <span className="text-sm text-gray-900 dark:text-white">
        {value}
      </span>
    </div>
  );
}

function SummaryCard({ icon, label, value, bgClass }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  bgClass: string;
}) {
  return (
    <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bgClass}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

const STATUS_CONFIG = {
  compliant: {
    label: 'Compliant',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    iconBgClass: 'bg-green-100 dark:bg-green-900/50',
    iconColorClass: 'text-green-600 dark:text-green-400',
    StatusIcon: CheckCircle2,
  },
  by_design: {
    label: 'Verified by Design',
    badgeClass: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    iconBgClass: 'bg-purple-100 dark:bg-purple-900/50',
    iconColorClass: 'text-purple-600 dark:text-purple-400',
    StatusIcon: ShieldCheck,
  },
  non_compliant: {
    label: 'Non-Compliant',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    iconBgClass: 'bg-red-100 dark:bg-red-900/50',
    iconColorClass: 'text-red-600 dark:text-red-400',
    StatusIcon: XCircle,
  },
  error: {
    label: 'Check Failed',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    iconBgClass: 'bg-amber-100 dark:bg-amber-900/50',
    iconColorClass: 'text-amber-600 dark:text-amber-400',
    StatusIcon: AlertCircle,
  },
} as const;

function ComplianceControlCard({ control }: { control: ComplianceControl }) {
  const config = STATUS_CONFIG[control.status];
  const icon = CONTROL_ICONS[control.control_id] ?? <Shield className="h-4 w-4" />;
  const frameworks = control.frameworks;
  const hasFrameworks = frameworks && (frameworks.cis || frameworks.nist_csf || frameworks.soc2);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-start gap-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0 ${config.iconBgClass}`}>
          <span className={config.iconColorClass}>{icon}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {control.title}
            </p>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.badgeClass}`}>
              {config.label}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {control.description}
          </p>

          {/* Details (verification result) */}
          {control.details && control.status !== 'by_design' && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 italic">
              {control.details}
            </p>
          )}

          {/* Framework references & timestamp row */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {hasFrameworks && (
              <>
                {frameworks.cis && (
                  <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                    {frameworks.cis}
                  </span>
                )}
                {frameworks.nist_csf && (
                  <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                    NIST {frameworks.nist_csf}
                  </span>
                )}
                {frameworks.soc2 && (
                  <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                    SOC 2 {frameworks.soc2}
                  </span>
                )}
              </>
            )}
            {control.checked_at && (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 ml-auto"
                title={formatFullTimestamp(control.checked_at)}
              >
                <Clock className="h-2.5 w-2.5" />
                {formatRelativeTime(control.checked_at)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SecurityControlCard({ icon, title, description }: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0 bg-blue-100 dark:bg-blue-900/50">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {title}
            </p>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              Planned
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}
